import type { DefaultOptions } from '@apollo/client';
import { ApolloClient, ApolloProvider, from, HttpLink, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

import { getMainDefinition } from '@apollo/client/utilities';
import React, { useMemo } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import config from '@/config';

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore'
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  },
  mutate: {
    fetchPolicy: 'no-cache', // Since all requests are currently used to update the board state, we shouldn't cache any responses.
    errorPolicy: 'all'
  }
};

export const ApolloProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const { token } = useAuth();

  const client = useMemo(() => {
    const httpLink = new HttpLink({
      uri: config.domain.http
    });

    let activeSocket: any, timedOut: NodeJS.Timeout;

    const wsLink = new GraphQLWsLink(
      createClient({
        url: config.domain.ws,

        // Ref: https://the-guild.dev/graphql/ws/docs/interfaces/client.ClientOptions#lazyclosetimeout
        lazy: true,

        // Ref: https://the-guild.dev/graphql/ws/docs/interfaces/client.ClientOptions#lazyclosetimeout
        lazyCloseTimeout: 30 * 1000, // 30 seconds

        // Ref: https://the-guild.dev/graphql/ws/docs/interfaces/client.ClientOptions#keepalive
        keepAlive: 10 * 1000, // 30,
        on: {
          connected: (socket) => (activeSocket = socket),
          ping: (received) => {
            if (!received)
              // sent
              timedOut = setTimeout(() => {
                if (activeSocket.readyState === WebSocket.OPEN)
                  activeSocket.close(4408, 'Request Timeout');
              }, 5_000); // wait 5 seconds for the pong and then close the connection
          },
          pong: (received) => {
            if (received) clearTimeout(timedOut); // pong is received, clear connection close timeout
          }
        },
        connectionParams: async () => {
          const authorization = await token();
          return {
            authorization
          };
        }
      })
    );

    const httpAuthMiddleware = setContext(async (_, { headers }) => {
      const authorization = await token();

      return {
        headers: {
          ...headers,
          Authorization: authorization
        }
      };
    });

    // Here, we use a split functiont to "route" a request to a specific
    // network link. In this case, if the request is a Subscription, it will
    // be sent to the wsLink, otherwise it will be sent to the httpLink.
    // This is to keep network connections optimized and avoid unnecessary
    // overhead. This is especially useful for mobile devices.
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      from([httpAuthMiddleware, httpLink])
    );

    const apolloClient = new ApolloClient({
      cache: new InMemoryCache(),
      link: splitLink,
      defaultOptions
    });

    return apolloClient;
  }, [token]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
